import { usesHostedBilling } from '@/lib/appDistribution';
import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useModalStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import CompactBalanceBar from '@/components/Credits/CompactBalanceBar';
import CreditPacksSection from '@/components/Credits/CreditPacksSection';
import CreditsFAQs from '@/components/Credits/CreditsFAQs';
import PremiumUpsellRow from '@/components/Credits/PremiumUpsellRow';
import AutoTopUpOfferRow from '@/components/Credits/AutoTopUpOfferRow';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { PLUS_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';

const createStyles = (colors, isXS) => StyleSheet.create({
  container: {
    width: '100%',
    flex: 1,
    ...(Platform.OS === 'macos' ? { flexBasis: 'auto', flexShrink: 0 } : Platform.OS !== 'web' ? { flexBasis: 0, minHeight: 0 } : {}),
  },
  content: {
    flex: 1,
    ...(Platform.OS === 'macos' && { flexBasis: 'auto', flexShrink: 0 }),
    padding: isXS ? 16 : 24,
    gap: 16,
  },
  footer: {
    gap: 10,
    paddingTop: 5,
  },
  plusFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 20,
    paddingBottom: 15,
    paddingHorizontal: 0,
    gap: 12,
    borderTopWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  plusFooterTextContainer: {
    flex: 1,
  },
});

function CreditsPurchaseModalSpeed({
  isPlus,
  isPlusPremium,
  isIapSubscriber,
  onClose,
  totalAvailableCredits,
  monthlyCredits,
  promoCode,
}) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);
  const { openModal } = useModalStore(state => state.actions);

  // Plus Premium is web-only; never upsell on top of App Store / Play Plus.
  const showPremiumUpsell = Boolean(isPlus && !isPlusPremium && !isIapSubscriber && usesHostedBilling());

  const resetDate = monthlyCredits?.resetDate;

  const handleUpgrade = useCallback(() => {
    onClose?.();
    if (Platform.OS === 'ios' || Platform.OS === 'macos') {
      openModal('PricingModal');
    } else {
      openModal('PricingModal');
    }
  }, [onClose, openModal]);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <CompactBalanceBar
          totalAvailableCredits={totalAvailableCredits}
          resetDate={resetDate}
          withInfoButton={false}
          isPlus={isPlus}
        />

        <CreditPacksSection promoCode={promoCode} />

        <View style={styles.footer}>
          {!isPlus && (
            <View style={styles.plusFooter}>
              <Icon name="sparkles" size={18} color={colors.text_color_primary} solid />
              <View style={styles.plusFooterTextContainer}>
                <Text size="n" weight="semibold" color="primary">
                  Free messages + {PLUS_MONTHLY_CREDITS_DISPLAY} monthly credits
                </Text>
                <Text size="xs" color="secondary">
                  with Plus — starting at $16.67/mo
                </Text>
              </View>
              <Button
                variant="plus"
                text="Upgrade"
                size="small"
                onPress={handleUpgrade}
              />
            </View>
          )}

          {showPremiumUpsell && (
            <PremiumUpsellRow onClose={onClose} />
          )}

          <AutoTopUpOfferRow />

          <CreditsFAQs />
        </View>
      </View>
    </View>
  );
}

export default React.memo(CreditsPurchaseModalSpeed);
